import React, { useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Loader2 } from 'lucide-react';

/**
 * La date du jour au format `YYYY-MM-DD`, dans le fuseau du lecteur.
 *
 * Surtout pas `toISOString()`, qui rend la date UTC : passé minuit à Paris en
 * été, il désigne encore la veille et le lien ouvrirait les lectures d'hier.
 */
const todayLocalIso = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Les sites de lectures du jour qui existent dans la langue du lecteur.
 *
 * Lire les lectures du jour dans sa propre langue prime sur le fait qu'elles
 * viennent de chez nous : TheoFons ne publie qu'en français et en anglais, et
 * ces trois-là valent mieux qu'un renvoi vers l'anglais (#420).
 *
 * Aucun n'accepte de date : ils ouvrent leur propre jour courant. C'est le prix
 * de la langue.
 *
 * L'allemand ne pointe plus sur `katholisch.de/stundenbuch`, qui donnait la
 * liturgie des heures et non les lectures de la messe. Le Schott de l'archiabbaye
 * de Beuron est le pendant allemand de l'AELF — vérifié le 12/08/2026, il porte
 * bien « ERSTE Lesung » et « EVANGELIUM ».
 */
const NATIVE_LANGUAGE_SITES: { [key: string]: string } = {
  es: 'https://bible.usccb.org/es/lectura-diaria-biblia',
  it: 'https://www.chiesacattolica.it/liturgia-del-giorno/',
  de: 'https://erzabtei-beuron.de/schott/',
};

/**
 * Où mènent les « Lectures du jour ».
 *
 * Le français et l'anglais vont chez nous, sur TheoFons, seul à accepter une
 * date : on ouvre donc directement le jour voulu, ce qu'aucun site tiers ne
 * permet. L'espagnol, l'italien et l'allemand gardent leur site dans leur
 * langue. Le reste — `ru`, `el`, et tout ce qui n'est pas prévu — va sur
 * TheoFons en anglais, là où l'ancienne table le renvoyait en silence sur un
 * site français.
 *
 * Le slash final est celui de la forme canonique. L'omettre coûte un 301.
 */
export const getDailyReadingsUrl = (language: string) => {
  const native = NATIVE_LANGUAGE_SITES[language];
  if (native) return native;

  const lang = language === 'fr' ? 'fr' : 'en';
  return `https://theofons.net/${lang}/lectionnaire/?date=${todayLocalIso()}`;
};

const DailyReadingsRedirect = () => {
  useEffect(() => {
    let detectedLang;
    
    // Check if URL path contains language parameter and matches daily-readings or daily-readings-redirect
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    if (
      pathSegments.length >= 2 &&
      (pathSegments[1] === 'daily-readings' || pathSegments[1] === 'daily-readings-redirect')
    ) {
      detectedLang = pathSegments[0].toLowerCase();
    } else if (
      pathSegments.length >= 1 &&
      (pathSegments[0] === 'daily-readings' || pathSegments[0] === 'daily-readings-redirect')
    ) {
      detectedLang = null;
    }

    // Only fallback to browser language if no language was detected in the URL
    // Plus de liste de langues « supportées » : la destination n'en distingue
    // que deux, et le repli sur `fr` envoyait un lecteur russe ou grec sur un
    // site français (#420).
    if (!detectedLang) {
      detectedLang = navigator.language.slice(0, 2).toLowerCase();
    }

    // Tout de suite, et non plus après 1,5 s : cette attente se justifiait
    // quand on quittait le site pour un tiers, elle ne sert plus à rien.
    // `replace` plutôt que `href` pour ne pas laisser cette page dans
    // l'historique — sans quoi le retour arrière relancerait la redirection.
    window.location.replace(getDailyReadingsUrl(detectedLang));
  }, []);

  return (
    <div className="min-h-screen bg-amber-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center space-y-4 text-amber-900">
            <Loader2 className="h-8 w-8 animate-spin" />
            {/* Redirection immédiate : cet écran ne se voit plus qu'en cas de
                lenteur. D'où le retrait de la ligne « Detected language »,
                qui était une trace de mise au point montrée à l'utilisateur. */}
            <p className="text-center">Redirecting to daily readings...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyReadingsRedirect;
